import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TenantVm, TenantListVm, CreateTenantDto, UpdateTenantDto, PagedResponse, TenantStatus } from '../models';
import { TenantModuleDatabaseVm, ModuleDatabaseStatus, ModuleTier } from '../models';
import { enumName, enumIndex } from '../utils/enum.util';

@Injectable({ providedIn: 'root' })
export class TenantService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/tenants`;

  private normalizeList<T extends { status: TenantStatus }>(t: T): T {
    return { ...t, status: enumName(TenantStatus, t.status) };
  }

  private normalizeDb(db: TenantModuleDatabaseVm): TenantModuleDatabaseVm {
    return { ...db, status: enumName(ModuleDatabaseStatus, db.status), tier: enumName(ModuleTier, db.tier) };
  }

  private normalize(t: TenantVm): TenantVm {
    return { ...this.normalizeList(t), moduleDatabases: (t.moduleDatabases ?? []).map((db) => this.normalizeDb(db)) };
  }

  list(params?: { search?: string; status?: TenantStatus; page?: number; pageSize?: number }): Observable<PagedResponse<TenantListVm>> {
    let hp = new HttpParams();
    if (params?.search) hp = hp.set('search', params.search);
    const status = enumIndex(TenantStatus, params?.status);
    if (status != null) hp = hp.set('status', status);
    if (params?.page) hp = hp.set('page', params.page.toString());
    if (params?.pageSize) hp = hp.set('pageSize', params.pageSize.toString());
    return this.http.get<PagedResponse<TenantListVm>>(this.base, { params: hp }).pipe(
      map((res) => ({ ...res, items: res.items.map((t) => this.normalizeList(t)) })),
    );
  }

  get(tenantId: string): Observable<TenantVm> {
    return this.http.get<TenantVm>(`${this.base}/${tenantId}`).pipe(map((t) => this.normalize(t)));
  }

  create(dto: CreateTenantDto): Observable<TenantVm> {
    return this.http.post<TenantVm>(this.base, dto).pipe(map((t) => this.normalize(t)));
  }

  update(tenantId: string, dto: UpdateTenantDto): Observable<TenantVm> {
    return this.http.put<TenantVm>(`${this.base}/${tenantId}`, dto).pipe(map((t) => this.normalize(t)));
  }

  suspend(tenantId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/${tenantId}/suspend`, {});
  }

  reactivate(tenantId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/${tenantId}/reactivate`, {});
  }

  delete(tenantId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${tenantId}`);
  }
}
